import { currentAccessToken } from "@/lib/session"
import { ApiError } from "@/types/api"
import { cacheUploadedBlob } from "@/services/mediaCacheService"
import { captureSession } from "@/services/submissionConcurrency"
import { useAuthStore } from "@/store/authStore"

import { API_BASE_URL } from "@/lib/apiConfig"
export { API_BASE_URL } from "@/lib/apiConfig"

type ApiEnvelope<T> = {
  success?: boolean
  Success?: boolean
  message?: string
  Message?: string
  errors?: string[]
  Errors?: string[]
  data?: T
  Data?: T
  code?: string
  Code?: string
}

export type BlobUploadResponse = {
  url: string
  /** Id da linha no registro de arquivos armazenados (StoredFile). Aditivo — clientes antigos ignoram. */
  storedFileId?: string
  /** Tamanho autoritativo em bytes, medido no servidor durante o upload. */
  sizeBytes?: number
}

/**
 * Uploads e downloads de blob usam `fetch` direto (multipart / resposta binária), fora
 * do `http.ts`. Por isso a renovação do token é feita aqui, antes da chamada.
 */
export async function getAccessToken(): Promise<string | null> {
  return currentAccessToken()
}

async function parseEnvelope<T>(response: Response, fallbackMessage: string): Promise<T> {
  // Diagnóstico temporário (ver CHANGELOG v0.9.4.5): quando o corpo não é o
  // envelope esperado (ex.: ProblemDetails automático do ASP.NET por falha de
  // model binding), `envelope` fica `undefined` e a mensagem cai no fallback
  // genérico sem pista nenhuma do motivo real — por isso lemos o texto cru
  // separadamente antes de tentar `response.json()`.
  const rawText = !response.ok ? await response.clone().text().catch(() => "") : ""
  const payload = await response.json().catch(() => undefined) as ApiEnvelope<T> | T | undefined
  const envelope = payload as ApiEnvelope<T> | undefined
  const success = envelope?.success ?? envelope?.Success

  if (!response.ok || success === false) {
    const message = envelope?.message ?? envelope?.Message
      ?? (rawText ? `${fallbackMessage} [HTTP ${response.status}: ${rawText.slice(0, 500)}]` : fallbackMessage)
    throw new ApiError(
      message,
      response.status === 401
        ? "UNAUTHORIZED"
        : response.status === 403
          ? "FORBIDDEN"
          : response.status === 400 || response.status === 422
            ? "VALIDATION"
            : "UNKNOWN",
      response.status,
      envelope?.errors ?? envelope?.Errors ?? [],
    )
  }

  if (payload && typeof payload === "object" && "data" in payload) {
    return (payload as ApiEnvelope<T>).data as T
  }

  if (payload && typeof payload === "object" && "Data" in payload) {
    return (payload as ApiEnvelope<T>).Data as T
  }

  return payload as T
}

const MIME_EXTENSION: Record<string, string> = {
  "video/mp4": "mp4",
  "video/webm": "webm",
  "video/quicktime": "mov",
  "video/x-matroska": "mkv",
  "video/3gpp": "3gp",
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/gif": "gif",
  "image/heic": "heic",
  "application/pdf": "pdf",
}

function extensionForMime(mimeType: string | undefined | null): string | null {
  // Item QA (2026-09-16): "TypeError: e.split is not a function" no envio — um `.type` de
  // Blob/File chegou aqui truthy mas não-string (blob "sobrevivendo" retorno de IndexedDB
  // no WebView Android, sem manter o protótipo nativo). `!mimeType` sozinho não cobre isso.
  if (!mimeType || typeof mimeType !== "string") return null
  const base = mimeType.split(";", 1)[0]!.trim().toLowerCase()
  if (MIME_EXTENSION[base]) return MIME_EXTENSION[base]
  const subtype = base.includes("/") ? base.slice(base.indexOf("/") + 1) : ""
  return /^[a-z0-9]{2,5}$/.test(subtype) ? subtype : null
}

const EXTENSION_MIME: Record<string, string> = {
  mp4: "video/mp4",
  m4v: "video/mp4",
  webm: "video/webm",
  mov: "video/quicktime",
  qt: "video/quicktime",
  mkv: "video/x-matroska",
  "3gp": "video/3gpp",
  "3gpp": "video/3gpp",
  ogv: "video/ogg",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  gif: "image/gif",
  heic: "image/heic",
  pdf: "application/pdf",
}

const GENERIC_BINARY_MIME = new Set(["", "application/octet-stream", "binary/octet-stream", "application/binary"])

function mimeFromUrlExtension(url: string): string | null {
  const path = url.split(/[?#]/, 1)[0] ?? url
  const name = path.slice(path.lastIndexOf("/") + 1)
  const dot = name.lastIndexOf(".")
  if (dot < 0) return null
  return EXTENSION_MIME[name.slice(dot + 1).toLowerCase()] ?? null
}

/**
 * O proxy autenticado `/api/blob` responde `application/octet-stream` para vídeo
 * (o switch de Content-Type do backend não cobre `.mp4`/`.webm`). O `<img>` ainda
 * adivinha o formato, mas `<video>`/`<audio>` respeitam o MIME do blob e recusam
 * reproduzir um octet-stream — era a causa de "vídeo não reproduz na tela de
 * resposta". Quando o tipo chega genérico, derivamos um MIME real pela extensão
 * da URL de origem (o blob é salvo como `{guid}.{ext}` no upload).
 */
export function normalizeBlobType(blob: Blob, sourceUrl: string): Blob {
  const current = typeof blob.type === "string" ? (blob.type.split(";", 1)[0] ?? "").trim().toLowerCase() : ""
  if (!GENERIC_BINARY_MIME.has(current)) return blob
  const inferred = mimeFromUrlExtension(sourceUrl)
  return inferred ? blob.slice(0, blob.size, inferred) : blob
}

const MAGIC_BYTES_EXTENSION: { ext: string; offset: number; bytes: number[] }[] = [
  { ext: "jpg", offset: 0, bytes: [0xff, 0xd8, 0xff] },
  { ext: "png", offset: 0, bytes: [0x89, 0x50, 0x4e, 0x47] },
  { ext: "gif", offset: 0, bytes: [0x47, 0x49, 0x46, 0x38] },
  { ext: "webp", offset: 8, bytes: [0x57, 0x45, 0x42, 0x50] }, // "RIFF"....\"WEBP\"
  { ext: "mp4", offset: 4, bytes: [0x66, 0x74, 0x79, 0x70] }, // ....\"ftyp\"
  { ext: "pdf", offset: 0, bytes: [0x25, 0x50, 0x44, 0x46] }, // %PDF
]

/**
 * Último recurso quando nem `.name` nem `.type` sobrevivem até aqui (item QA
 * 2026-09-16: POCO/Xiaomi rejeitando upload com "Arquivo sem extensão não é
 * permitido" mesmo depois dos ajustes em imageMedia.ts/imageCompression.ts —
 * suspeita de shim de `File` diferente por aparelho/WebView). Olha os primeiros
 * bytes do arquivo (assinatura de formato), que não dependem de nenhuma
 * propriedade JS potencialmente corrompida pelo motor nativo.
 */
async function detectExtensionFromBytes(file: Blob): Promise<string | null> {
  try {
    const head = new Uint8Array(await file.slice(0, 12).arrayBuffer())
    const match = MAGIC_BYTES_EXTENSION.find(({ offset, bytes }) =>
      bytes.every((byte, i) => head[offset + i] === byte),
    )
    return match?.ext ?? null
  } catch {
    return null
  }
}

/**
 * O backend recusa (e responde HTTP 500) qualquer upload cujo nome de arquivo não
 * tenha extensão. Vídeos gravados pelo `MediaRecorder` chegam como `Blob` nativo e
 * mídia recuperada da fila offline é remontada como `File` sem extensão — nos dois
 * casos `FormData.append(name, blob)` mandaria `filename="blob"` e o envio falhava
 * (itens QA 3 e 5a). Aqui derivamos um nome com extensão a partir do MIME (e, se
 * isso também falhar, da assinatura de bytes do arquivo) e o passamos explicitamente
 * como 3º argumento do `append`.
 */
async function resolveUploadFileName(file: File | Blob): Promise<string> {
  const rawName = typeof (file as File).name === "string" ? (file as File).name.trim() : ""
  if (rawName && /\.[a-z0-9]{2,5}$/i.test(rawName)) return rawName

  const ext = extensionForMime(file.type) ?? (await detectExtensionFromBytes(file))
  const base = rawName || `upload-${Date.now()}`
  return ext ? `${base}.${ext}` : base
}

// Vídeo/foto sem compressão suficiente ainda pode passar de dezenas de MB em
// conexão móvel — bem acima do timeout de 15s do cliente HTTP principal.
const UPLOAD_TIMEOUT_MS = 120_000
const UPLOAD_MAX_RETRIES = 1
const UPLOAD_RETRY_BASE_MS = 1_000

function isDisconnected(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms))
}

/**
 * `window.fetch` nativo salvo pelo `native-bridge.js` do Capacitor ANTES de
 * sobrescrevê-lo — só existe quando `CapacitorHttp.enabled` está ligado
 * (ver `capacitor.config.json`). Com o plugin habilitado, `fetch` global
 * passa a rotear POST/PUT/DELETE pela ponte nativa, que serializa mal um
 * corpo `FormData` multipart (o campo "file" chegava ausente no backend —
 * "The file field is required." mesmo com um Blob válido no cliente).
 * `fetch` "puro" dentro do app não bastava para escapar disso, porque o
 * patch troca o identificador global que todo código (inclusive este)
 * enxerga como `fetch`.
 */
function nativeFetch(): typeof fetch {
  const win = typeof window !== "undefined" ? (window as unknown as { CapacitorWebFetch?: typeof fetch }) : undefined
  return win?.CapacitorWebFetch ?? fetch
}

/**
 * Fora do `http.ts` — corpo multipart, e `CapacitorHttp` (usado pelo resto do app nativo
 * pra contornar CORS) não lida bem com upload de arquivo, então isso continua no fetch
 * nativo do WebView (ver `nativeFetch`) mesmo dentro do app. Por isso essa função precisa
 * da MESMA classificação de erro que `dispatchWeb` faz: sem ela, uma falha de rede no meio
 * do upload chegava como um `TypeError` cru — que `err instanceof ApiError` (usado nos
 * formulários pra decidir se protege o preenchimento na fila offline) não reconhece — e o
 * preenchimento inteiro, foto incluída, era descartado em vez de entrar na fila de reenvio.
 */
async function attemptUpload<T>(url: URL, formData: FormData, accessToken: string | null): Promise<T> {
  if (isDisconnected()) {
    throw new ApiError("Sem conexão com a internet.", "NETWORK", 0)
  }

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS)

  let response: Response
  try {
    response = await nativeFetch()(url, {
      method: "POST",
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      body: formData,
      signal: controller.signal,
    })
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      throw new ApiError("Tempo esgotado ao enviar o arquivo.", "TIMEOUT", 0)
    }
    if (err instanceof TypeError) {
      const online = !isDisconnected()
      throw new ApiError(
        online ? "Não foi possível conectar ao servidor." : "Sem conexão com a internet.",
        online ? "UNREACHABLE" : "NETWORK",
        0,
      )
    }
    throw err
  } finally {
    clearTimeout(timer)
  }

  return parseEnvelope<T>(response, "Erro ao enviar arquivo.")
}

export async function uploadMultipart<T>(
  path: string,
  file: File,
  options: { fieldName?: string; query?: Record<string, string | null | undefined> } = {},
): Promise<T> {
  const check = captureSession()
  const url = new URL(`${API_BASE_URL}${path.startsWith("/") ? path : `/${path}`}`)
  Object.entries(options.query ?? {}).forEach(([key, value]) => {
    if (value) url.searchParams.set(key, value)
  })

  const formData = new FormData()
  formData.append(options.fieldName ?? "file", file, await resolveUploadFileName(file))

  const accessToken = await getAccessToken()

  for (let attempt = 0; ; attempt++) {
    try {
      check()
      return await attemptUpload<T>(url, formData, accessToken)
    } catch (err) {
      const retryable = err instanceof ApiError && err.retryable
      if (!retryable || attempt >= UPLOAD_MAX_RETRIES) throw err
      await sleep(UPLOAD_RETRY_BASE_MS * (attempt + 1))
    }
  }
}

export async function uploadBlob(file: File, workspaceId?: string | null): Promise<BlobUploadResponse> {
  const userId = useAuthStore.getState().user?.id
  const response = await uploadMultipart<BlobUploadResponse & { Url?: string; StoredFileId?: string; SizeBytes?: number }>("/api/blob", file, {
    query: { workspaceId },
  })

  const url = response?.url ?? response?.Url ?? ""
  if (typeof url !== "string" || !url.trim()) throw new ApiError("Upload did not return a media URL.", "SERVER")
  // Best-effort, não bloqueia o retorno do upload: guarda uma cópia local pra
  // `useAuthenticatedImage` conseguir mostrar/editar essa mídia depois mesmo sem rede
  // (ex.: retomar um preenchimento pausado offline — ver mediaCacheService.ts).
  if (url && userId) void cacheUploadedBlob(url, file, userId)

  const storedFileId = response?.storedFileId ?? response?.StoredFileId
  const sizeBytes = response?.sizeBytes ?? response?.SizeBytes
  return { url, storedFileId, sizeBytes }
}

export async function downloadBlob(url: string): Promise<Blob> {
  const requestUrl = new URL(`${API_BASE_URL}/api/blob`)
  requestUrl.searchParams.set("url", url)

  const accessToken = await getAccessToken()
  const response = await fetch(requestUrl, {
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  })

  if (!response.ok) {
    throw new ApiError("Erro ao baixar arquivo.", response.status === 401 ? "UNAUTHORIZED" : "UNKNOWN", response.status)
  }

  return normalizeBlobType(await response.blob(), url)
}

export async function deleteBlob(url: string): Promise<void> {
  const requestUrl = new URL(`${API_BASE_URL}/api/blob`)
  requestUrl.searchParams.set("url", url)

  const accessToken = await getAccessToken()
  const response = await fetch(requestUrl, {
    method: "DELETE",
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  })

  await parseEnvelope<void>(response, "Erro ao remover arquivo.")
}
